import Papa from 'papaparse';
import type { Parser } from 'papaparse';
import * as XLSX from 'xlsx';

const MAX_TABULAR_PREVIEW_ROWS = 100;
const MAX_TABULAR_PREVIEW_COLUMNS = 25;
const TABULAR_PREVIEW_ROW_WINDOW = MAX_TABULAR_PREVIEW_ROWS + 2;
const TABULAR_PREVIEW_COLUMN_WINDOW = MAX_TABULAR_PREVIEW_COLUMNS + 1;
const CSV_PREVIEW_FILE_ERROR = 'Failed to load CSV preview.';
const CSV_PREVIEW_PARSE_ERROR = 'Failed to parse CSV preview.';
const EXCEL_PREVIEW_FILE_ERROR = 'Failed to load Excel preview.';
const EXCEL_PREVIEW_PARSE_ERROR = 'Failed to parse Excel preview.';

const IMAGE_KEY_PATTERN = /\.(png|jpe?g|gif|bmp|webp|tiff?)$/i;
const PDF_KEY_PATTERN = /\.pdf$/i;
const CSV_KEY_PATTERN = /\.csv$/i;
const EXCEL_KEY_PATTERN = /\.(xlsx|xlsm)$/i;

export type AttachmentPreviewType =
  | 'none'
  | 'image'
  | 'pdf'
  | 'csv'
  | 'excel'
  | 'unsupported';

export interface TabularPreviewData {
  columns: string[];
  rows: string[][];
  truncatedRows: boolean;
  truncatedColumns: boolean;
}

export type CsvPreviewData = TabularPreviewData;

export interface ExcelPreviewData extends TabularPreviewData {
  sheetName: string;
}

export type CsvPreviewErrorMode = 'csv-load' | 'csv-parse';
export type ExcelPreviewErrorMode = 'excel-load' | 'excel-parse';
type PreviewErrorMode = CsvPreviewErrorMode | ExcelPreviewErrorMode;

interface PreviewError extends Error {
  previewMode: PreviewErrorMode;
}

export interface CsvPreviewError extends Error {
  previewMode: CsvPreviewErrorMode;
}

export interface ExcelPreviewError extends Error {
  previewMode: ExcelPreviewErrorMode;
}

export const getAttachmentPreviewType = (
  key: string
): AttachmentPreviewType => {
  if (!key) return 'none';
  if (IMAGE_KEY_PATTERN.test(key)) return 'image';
  if (PDF_KEY_PATTERN.test(key)) return 'pdf';
  if (CSV_KEY_PATTERN.test(key)) return 'csv';
  if (EXCEL_KEY_PATTERN.test(key)) return 'excel';
  return 'unsupported';
};

const createPreviewError = (
  message: string,
  previewMode: PreviewErrorMode,
  cause?: unknown
): PreviewError => {
  const error = cause instanceof Error ? cause : new Error(message);
  error.message = message;
  return Object.assign(error, { previewMode });
};

const buildTabularPreview = (parsedRows: string[][]): TabularPreviewData => {
  const headerRow = parsedRows[0] ?? [];
  const dataRows = parsedRows.slice(1);
  const truncatedRows = dataRows.length > MAX_TABULAR_PREVIEW_ROWS;
  const visibleRows = dataRows.slice(0, MAX_TABULAR_PREVIEW_ROWS);
  const widestRow = [headerRow, ...visibleRows].reduce(
    (maxColumns, row) => Math.max(maxColumns, row.length),
    0
  );
  const truncatedColumns = widestRow > MAX_TABULAR_PREVIEW_COLUMNS;
  const visibleColumnCount = Math.min(widestRow, MAX_TABULAR_PREVIEW_COLUMNS);
  const columns = Array.from(
    { length: visibleColumnCount },
    (_value, index) => headerRow[index] || `Column ${index + 1}`
  );
  const rows = visibleRows.map((row) =>
    columns.map((_value, index) => row[index] ?? '')
  );

  return {
    columns,
    rows,
    truncatedRows,
    truncatedColumns,
  };
};

export const loadCsvPreview = (
  signedUrl: string,
  onParserReady?: (parser: Parser) => void
) =>
  new Promise<CsvPreviewData>((resolve, reject) => {
    const parsedRows: string[][] = [];
    let settled = false;

    const settleResolve = () => {
      if (settled) return;
      settled = true;
      resolve(buildTabularPreview(parsedRows));
    };

    const settleReject = (
      message: string,
      previewMode: CsvPreviewErrorMode,
      cause?: unknown
    ) => {
      if (settled) return;
      settled = true;
      reject(createPreviewError(message, previewMode, cause));
    };

    Papa.parse<string[]>(signedUrl, {
      download: true,
      worker: true,
      preview: TABULAR_PREVIEW_ROW_WINDOW,
      skipEmptyLines: 'greedy',
      step: (results, parser) => {
        onParserReady?.(parser);
        if (results.errors.length > 0) {
          settleReject(
            results.errors[0]?.message || CSV_PREVIEW_PARSE_ERROR,
            'csv-parse'
          );
          parser.abort();
          return;
        }

        parsedRows.push(Array.isArray(results.data) ? results.data : []);

        if (parsedRows.length >= TABULAR_PREVIEW_ROW_WINDOW) {
          parser.abort();
          settleResolve();
        }
      },
      complete: () => {
        settleResolve();
      },
      error: (error) => {
        settleReject(
          error.message || CSV_PREVIEW_FILE_ERROR,
          'csv-load',
          error
        );
      },
    });
  });

const normalizeExcelPreviewRows = (rows: unknown[][]): string[][] =>
  rows.map((row) =>
    row.map((value) => {
      if (value == null) return '';
      return typeof value === 'string' ? value : String(value);
    })
  );

const getPreviewWorksheetRange = (worksheet: XLSX.WorkSheet) => {
  const worksheetRef = worksheet['!ref'];
  if (!worksheetRef) return undefined;

  const decodedRange = XLSX.utils.decode_range(worksheetRef);

  // SheetJS range objects use `s`/`e` for start/end and `c`/`r` for column/row.
  // Keep the worksheet start cell, then clamp the end cell to the preview window.
  return {
    s: decodedRange.s,
    e: {
      c: Math.min(
        decodedRange.e.c,
        decodedRange.s.c + TABULAR_PREVIEW_COLUMN_WINDOW - 1
      ),
      r: Math.min(
        decodedRange.e.r,
        decodedRange.s.r + TABULAR_PREVIEW_ROW_WINDOW - 1
      ),
    },
  };
};

export const loadExcelPreview = async (
  signedUrl: string
): Promise<ExcelPreviewData> => {
  let workbookBytes: ArrayBuffer;

  try {
    const response = await fetch(signedUrl);
    if (!response.ok) {
      throw new Error(`Failed to load workbook: ${response.status}`);
    }
    workbookBytes = await response.arrayBuffer();
  } catch (error) {
    throw createPreviewError(EXCEL_PREVIEW_FILE_ERROR, 'excel-load', error);
  }

  try {
    const workbook = XLSX.read(workbookBytes, {
      dense: true,
      sheets: 0,
      sheetRows: TABULAR_PREVIEW_ROW_WINDOW,
    });
    const sheetName = workbook.SheetNames[0];

    if (!sheetName) {
      throw new Error('Workbook has no readable worksheets.');
    }

    const worksheet = workbook.Sheets[sheetName];

    if (!worksheet) {
      throw new Error(`Worksheet ${sheetName} could not be loaded.`);
    }

    const parsedRows = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
      header: 1,
      raw: false,
      defval: '',
      blankrows: false,
      skipHidden: true,
      range: getPreviewWorksheetRange(worksheet),
    });

    return {
      sheetName,
      ...buildTabularPreview(normalizeExcelPreviewRows(parsedRows)),
    };
  } catch (error) {
    throw createPreviewError(
      error instanceof Error && error.message
        ? error.message
        : EXCEL_PREVIEW_PARSE_ERROR,
      'excel-parse',
      error
    );
  }
};
